import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  IconButton,
  Paper,
  Grid,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

// Group shortcuts by category for better organization

const ShortcutHelpModal = ({
  open,
  onClose,
  SHORTCUTS,
  SHORTCUT_CATEGORIES,
}) => {
  // Helper function to get shortcuts by category
  const getShortcutsByCategory = (categoryName) => {
    const category = SHORTCUT_CATEGORIES.find(
      (cat) => cat.name === categoryName,
    );
    if (!category) return [];

    return category.shortcuts
      .map((shortcutKey) => SHORTCUTS.find((s) => s.keys === shortcutKey))
      .filter(Boolean);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ m: 0, p: 2, backgroundColor: "var(--bg-paper)" }}>
        <Typography variant="h6" component="div" fontWeight="bold">
          Keyboard Shortcuts
        </Typography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: "var(--text-secondary)",
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ mb: 3 }}>
          <Typography variant="body1" gutterBottom>
            Use these keyboard shortcuts to navigate quickly through the
            application.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Note: Shortcuts work throughout the application except when typing
            in text fields.
          </Typography>
        </Box>

        {SHORTCUT_CATEGORIES.map((category) => (
          <Box key={category.name} sx={{ mb: 4 }}>
            <Typography
              variant="h6"
              gutterBottom
              sx={{
                borderBottom: "1px solid var(--border-color)",
                pb: 1,
                mb: 2,
              }}
            >
              {category.name}
            </Typography>

            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#f9f9f9" }}>
                    <TableCell width="30%">
                      <strong>Shortcut</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Description</strong>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {getShortcutsByCategory(category.name).map(
                    (shortcut, index) => (
                      <TableRow key={index} hover>
                        <TableCell>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                            }}
                          >
                            {shortcut.keys.split("+").map((key, i) => (
                              <React.Fragment key={i}>
                                <Box
                                  component="span"
                                  sx={{
                                    backgroundColor: "var(--bg-input)",
                                    border: "1px solid var(--border-color)",
                                    borderRadius: "4px",
                                    px: 1,
                                    py: 0.5,
                                    fontFamily: "monospace",
                                    fontSize: "0.875rem",
                                    boxShadow: "0 1px 1px rgba(0,0,0,0.1)",
                                    display: "inline-block",
                                    minWidth: "24px",
                                    textAlign: "center",
                                  }}
                                >
                                  {key.toUpperCase()}
                                </Box>
                                {i < shortcut.keys.split("+").length - 1 && (
                                  <Box component="span" sx={{ mx: 0.5 }}>
                                    +
                                  </Box>
                                )}
                              </React.Fragment>
                            ))}
                          </Box>
                        </TableCell>
                        <TableCell>{shortcut.description}</TableCell>
                      </TableRow>
                    ),
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        ))}

        <Box sx={{ mt: 3 }}>
          <Typography variant="h6" gutterBottom>
            Tips
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Box
                  component="span"
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    backgroundColor: "primary.main",
                    mr: 1.5,
                  }}
                />
                <Typography variant="body2">
                  Press <strong>Alt+H</strong> at any time to open this
                  shortcuts modal.
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Box
                  component="span"
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    backgroundColor: "primary.main",
                    mr: 1.5,
                  }}
                />
                <Typography variant="body2">
                  All shortcuts use the Alt key combined with another key for
                  quick access.
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ShortcutHelpModal;
